import { UseFormReturn } from 'react-hook-form'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Checkbox, F0DatePicker } from '@factorialco/f0-react'
import { Features } from 'generated/featureDefinitions'

import { i18nDateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { ShiftsDeleteSchemaType } from 'modules/shift_management/lib/forms/ShiftsDelete/schema'
import { maxDate, minDate } from 'modules/shift_management/lib/getMinMaxDates'

interface Props {
  form: UseFormReturn<ShiftsDeleteSchemaType>
  label: string
  selectableEmployeeIds: string[]
  onCheckboxClick: () => void
  destroyBackUpShifts: boolean
  setDestroyAutomaticOnly: () => void
  destroyAutomaticOnly: boolean
}

const FormContent = ({
  form,
  label,
  selectableEmployeeIds,
  onCheckboxClick,
  destroyBackUpShifts,
  setDestroyAutomaticOnly,
  destroyAutomaticOnly,
}: Props) => {
  const { getValues, setValue, watch } = form
  const employeeSelection = watch('employeeSelection')
  const { startAt, endAt } = getValues()

  const employeeCount = employeeSelection.type === 'manual' ? employeeSelection.ids.length : ''

  const [{ hasFeatureEntitlement, hasFeatureFlag }] = useEnabledFeatures()
  const isAutomationEnabled = hasFeatureEntitlement(Features.shift_management_automatic_planning)

  const showWarning = (): boolean => {
    return (employeeCount === '' || employeeCount > 0) && !!endAt && !!startAt
  }

  const hasBackUpShifts = hasFeatureFlag('shift_management_backup_shifts')

  return (
    <Box gap="s16">
      <F0DatePicker
        size="md"
        label={i18n.t('shift_management.coverage_section.unassigned_shift.date_range')}
        value={{
          granularity: 'range',
          value: {
            from: startAt,
            to: endAt,
          },
        }}
        onChange={(value) => {
          if (!value?.value) return
          setValue('startAt', value.value.from)
          setValue('endAt', value.value.to)
        }}
        minDate={minDate.toDate()}
        maxDate={maxDate.toDate()}
      />
      <EmployeeSelectorV2
        label={label}
        onChange={(value) => {
          if (!value) return
          setValue('employeeSelection', value)
        }}
        value={employeeSelection.type === 'manual' ? employeeSelection.ids : []}
        multiple
        forcedFilters={{ employeeIds: selectableEmployeeIds }}
      />
      {hasBackUpShifts && (
        <F0Checkbox
          checked={destroyBackUpShifts}
          onCheckedChange={onCheckboxClick}
          title={i18n.t('shift_management.backup_shifts.delete')}
        />
      )}
      {isAutomationEnabled && (
        <F0Checkbox
          checked={destroyAutomaticOnly}
          onCheckedChange={setDestroyAutomaticOnly}
          title={i18n.t('shift_management.coverage_section.automation.delete')}
        />
      )}
      {showWarning() && (
        <Box
          paddingX="s24"
          paddingY="s16"
          background="champagne400"
          borderRadius={{
            all: 'abs008',
          }}
        >
          {i18n.tView('shift_management.bulk_delete.info', {
            start_date: <b>{i18nDateToString(startAt, 'backend')}</b>,
            end_date: <b>{i18nDateToString(endAt, 'backend')}</b>,

            count: (
              <b>
                {employeeCount}&nbsp;{i18n.t('common.employee_count.other')}
              </b>
            ),
          })}
        </Box>
      )}
    </Box>
  )
}

export { FormContent }
